import { Component, HostListener, Inject } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { ServiceInsightsPhotoObject } from 'src/app/features/service-insights/types/service-insights-export-photo.types';
import { ServiceInsightsphotoObjectDefault } from './export-photo-constants';
import { ServiceInsightsExportPhotoService } from 'src/app/features/service-insights/services/service-insights-export-photo-service';
import JSZip from 'jszip';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { AnyAaaaRecord } from 'dns';

/**
 * Parent component that renders the photo gallery used by the
 * Service Insights Details views: By Date, By Location, and By Issues.
 * @see ServiceDetailsTableComponent
 */
@Component({
    selector: 'service-insights-photo-gallery',
    templateUrl: './service-insights-photo-gallery.component.html',
    styleUrls: ['./service-insights-photo-gallery.component.scss'],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('.4s ease-out', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('.4s ease-in', style({ opacity: 0 })),
            ]),
        ]),
    ],
    standalone: false
})
export class ServiceInsightsPhotoGalleryComponent {
  serviceInsightsPhotoObject: ServiceInsightsPhotoObject =
    ServiceInsightsphotoObjectDefault;
  currentSlideIndex: number = 0;
  showArrows: boolean = true;
  photosPerPage: number = 8;
  currentPhotoIndex: number = 0;

  constructor(
    private _exportPhotoService: ServiceInsightsExportPhotoService,
    private _mixpanelService: MixpanelService,
    private _authenticationservice: AuthenticationService,
  ) {}

  ngAfterContentInit() {
    this._exportPhotoService.serviceInsightsPhotoObject.subscribe(
      (value) => (this.serviceInsightsPhotoObject = value),
    );
    this.adjustPhotosPerPage();
  }
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.adjustPhotosPerPage();
  }

  adjustPhotosPerPage() {
    const width = window.innerWidth;
    if (width < 768) {
      this.photosPerPage = 2;
    } else if (width >= 768 && width < 1300) {
      this.photosPerPage = 4;
    } else {
      this.photosPerPage = 8;
    }
    this.updateShowArrows();
  }
  navigateSlide(direction: number): void {
    const totalPages = Math.ceil(
      this.serviceInsightsPhotoObject.photoInfo.length / this.photosPerPage,
    );
    let currentPage = Math.floor(this.currentSlideIndex / this.photosPerPage);
    currentPage = (currentPage + direction + totalPages) % totalPages;
    this.currentSlideIndex = currentPage * this.photosPerPage;
    this.updateShowArrows();
  }

  updateShowArrows(): void {
    this.showArrows =
      this.serviceInsightsPhotoObject.photoInfo.length > this.photosPerPage;
  }
  onIconCloseClicked(): void {
    this._exportPhotoService.setIsExportPhotoView(false);
  }

  resetSelection() {
    this.serviceInsightsPhotoObject.photoInfo.forEach((photo) => {
      photo.selected = false;
    });
  }

  handleSelectClick(event: Event, id: number): void {
    this.serviceInsightsPhotoObject.photoInfo[id].selected = (
      event.target as HTMLInputElement
    ).checked;
  }

  onSelectAllClick(event: any) {
    this.serviceInsightsPhotoObject.photoInfo.forEach((photo) => {
      photo.selected = event.target.checked;
    });
  }
  onPhotoClick(id: number) {
    this.currentPhotoIndex = id;
  }

  getSelectedPhotosCount(): number {
    return this.serviceInsightsPhotoObject.photoInfo.filter(
      (photo) => photo.selected,
    ).length;
  }

  async downloadImagesAsZip() {
    // Function to sanitize file names by replacing unsupported characters
    const sanitizeFileName = (name: string) => {
      return name.replace(/[:]+/g, '_');
    };

    const fileName = prompt(
      'Please enter the zip filename for the download:',
      `${this.serviceInsightsPhotoObject?.endDate ?? ''}-${this.serviceInsightsPhotoObject?.accountName ?? ''}`,
    );
    if (fileName !== null) {
      const sanitizedFileName = sanitizeFileName(fileName); // Sanitize the file name
      const zip = new JSZip();
      const folder = zip.folder(sanitizedFileName); // Use sanitized file name for the folder

      // Filter the array to include only selected photos
      const selectedPhotos =
        this?.serviceInsightsPhotoObject?.photoInfo?.filter(
          (photo) => photo?.selected,
        ) ?? [];

      for (const [index, photo] of selectedPhotos.entries()) {
        const response = await fetch(photo.url);
        const blob = await response.blob();

        // Use sanitized file name for each image file
        if (folder !== null) {
          folder.file(
            `${sanitizeFileName(this.serviceInsightsPhotoObject?.endDate)}-${this.serviceInsightsPhotoObject?.accountName}-${index}.jpg`,
            blob,
            { binary: true },
          );
        }
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const tempLink = document.createElement('a');
      tempLink.href = URL.createObjectURL(content);
      tempLink.download = `${sanitizedFileName}.zip`; // Use sanitized file name for the download
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      this._mixpanelService.track('Export Multi Photo Gallery Downloaded', {
        AccountName: this.serviceInsightsPhotoObject.accountName,
        AccountNumber: this.serviceInsightsPhotoObject.accountNumber,
        ImpersonatedCustomer: this._authenticationservice.customerName ?? ' ',
        ImpersonatedUser: this._authenticationservice.userName ?? ' ',
      });
    }
  }
}
